<script lang="ts">
  import { crmLogin } from '../lib/api/queries.js';
  import { http } from '../lib/api/http.js';
  import { session } from '../lib/stores/session.svelte.js';

  let email = $state('');
  let password = $state('');
  let submitting = $state(false);
  let errorMessage = $state<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    if (submitting) return;
    errorMessage = null;
    submitting = true;
    try {
      const res = await crmLogin({ email: email.trim(), password });
      const first = res.memberships[0];
      if (!first) {
        errorMessage = 'No tenant membership for this account.';
        return;
      }
      http.setSessionToken(res.sessionToken);
      session.setReady({
        sessionToken: res.sessionToken,
        embedOrigins: [],
        tenant: {
          id: first.tenantId,
          code: first.tenantCode,
          name: first.tenantDisplayName,
        },
        user: {
          id: res.id,
          email: res.email,
          displayName: `${res.firstName} ${res.lastName}`.trim(),
          role: first.role,
        },
      });
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<div class="login-page">
  <form class="card" onsubmit={submit}>
    <h1>Operator sign in</h1>
    <p class="hint">Sign in with your operator credentials to access the inbox.</p>

    <label>
      <span>Email</span>
      <input
        type="email"
        autocomplete="username"
        bind:value={email}
        required
        disabled={submitting}
      />
    </label>

    <label>
      <span>Password</span>
      <input
        type="password"
        autocomplete="current-password"
        bind:value={password}
        required
        disabled={submitting}
      />
    </label>

    {#if errorMessage}
      <p class="error" role="alert">{errorMessage}</p>
    {/if}

    <button type="submit" disabled={submitting || !email || !password}>
      {submitting ? 'Signing in…' : 'Sign in'}
    </button>
  </form>
</div>

<style>
  .login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 24px;
    background: var(--widget-bg, oklch(99% 0.003 250));
  }
  .card {
    width: 100%;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 24px;
    border: 1px solid oklch(90% 0.01 250);
    border-radius: 12px;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
  }
  .hint {
    margin: 0;
    font-size: 12.5px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  input {
    font: inherit;
    padding: 8px 10px;
    border: 1px solid oklch(85% 0.012 250);
    border-radius: 8px;
    background: white;
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  input:focus {
    outline: 2px solid var(--widget-accent, oklch(60% 0.18 255));
    outline-offset: 1px;
    border-color: transparent;
  }
  button {
    margin-top: 4px;
    font: inherit;
    font-weight: 600;
    padding: 10px;
    border: 0;
    border-radius: 8px;
    background: var(--widget-accent, oklch(60% 0.18 255));
    color: white;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .error {
    margin: 0;
    font-size: 12.5px;
    color: oklch(45% 0.18 25);
  }
</style>
